import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { ASK_THREE_COPY, RATER_LINK_FOR, RATER_SHARE_MESSAGE, RATER_SUGGESTION } from "../lib/copy";
import type { RaterInvite, RatersView } from "../lib/types";

const STATUS_LABEL: Record<RaterInvite["status"], string> = {
  open: "Not started",
  started: "In progress",
  completed: "Done",
};

/** A phone, where a Text button opens Messages (desktops get Email instead). */
const isPhone = () => typeof navigator !== "undefined" && /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

/**
 * RaterInvites: the three rater links (GET /v1/takes/{takeId}/raters), each sent by text, the
 * phone's share sheet or email, or copied, with a message in the taker's voice. While the
 * endpoint isn't available, shows the suggestion and a disabled button.
 */
export function RaterInvites({
  takeId,
  resultKey,
  note,
  suggestion = RATER_SUGGESTION,
  compact = false,
}: {
  takeId: string;
  /** Result link key, when the page was opened from the results email. */
  resultKey?: string | null;
  note?: string | null;
  /** Pass null to hide the suggestion line (shown once already). */
  suggestion?: string | null;
  /** Inside the card at the top of the results, which shows its own progress. */
  compact?: boolean;
}) {
  const [raters, setRaters] = useState<RatersView | null | undefined>(undefined);

  useEffect(() => {
    let live = true;
    api
      .getRaters(takeId, resultKey)
      .then((r) => live && setRaters(r))
      .catch(() => live && setRaters(null));
    return () => {
      live = false;
    };
  }, [takeId, resultKey]);

  const invites = raters?.invites ?? [];
  const completed = raters?.completed ?? invites.filter((i) => i.status === "completed").length;
  const phone = isPhone();

  return (
    <div data-testid="rater-invites">
      {suggestion ? <p className="text-[17px] leading-snug">{suggestion}</p> : null}
      {note ? <p className="mt-3 text-[15px] text-muted">{note}</p> : null}
      {raters === undefined ? (
        <p role="status" className={`${compact ? "" : "mt-5 "}text-[15px] text-muted`}>
          Loading your links…
        </p>
      ) : invites.length === 0 ? (
        <>
          <button type="button" className={`btn btn-secondary ${compact ? "" : "mt-5 "}w-full`} disabled>
            Invite 3 people
          </button>
          {/* Not this device's (e.g. another profile, opened after a results email): say how to get them. */}
          {raters === null ? (
            <p className="mt-2 text-[14px] text-muted" data-testid="invites-elsewhere">
              To send these from this device, open the results email we sent you, or sign in with a link we&apos;ll email you.
            </p>
          ) : null}
        </>
      ) : (
        <>
          {compact ? null : (
            <p className="mt-5 text-[15px] font-semibold">
              {completed} of {invites.length} done
            </p>
          )}
          {raters?.describeBack && completed < invites.length ? (
            <p className={`${compact ? "" : "mt-3 "}text-pretty text-[15px] leading-snug`} data-testid="describe-back">
              {ASK_THREE_COPY.backTo(raters.describeBack)}
            </p>
          ) : null}
          <ul className={`${compact && !raters?.describeBack ? "" : "mt-3 "}flex flex-col gap-3`}>
            {[...invites]
              .sort((a, b) => a.slot - b.slot)
              .map((invite) => (
                <InviteRow key={invite.slot} invite={invite} phone={phone} onShared={(via) => api.markShared(takeId, codeOf(invite.url), via, resultKey)} />
              ))}
          </ul>
        </>
      )}
    </div>
  );
}

/** The link's code: its last path part (/r/<code>). */
const codeOf = (url: string) => url.replace(/[?#].*$/, "").split("/").filter(Boolean).at(-1) ?? "";

function InviteRow({ invite, phone, onShared }: { invite: RaterInvite; phone: boolean; onShared: (via: "text" | "share" | "copy") => void }) {
  const [copied, setCopied] = useState(false);
  const canShare = typeof navigator !== "undefined" && typeof navigator.share === "function";
  const forWhom = RATER_LINK_FOR[invite.slot];
  const title = forWhom ? `Link ${invite.slot}, for ${forWhom.charAt(0).toLowerCase()}${forWhom.slice(1)}` : `Link ${invite.slot}`;
  const message = `${RATER_SHARE_MESSAGE} ${invite.url}`;

  const copy = async () => {
    onShared("copy");
    try {
      await navigator.clipboard.writeText(message);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };
  const share = async () => {
    onShared("share");
    try {
      await navigator.share({ text: RATER_SHARE_MESSAGE, url: invite.url });
    } catch {
      // Cancelled or unsupported: nothing to do.
    }
  };
  const done = invite.status === "completed";
  const button = "btn min-h-11 flex-1 px-3 text-[14px] no-underline";

  return (
    // Finished ones stand out (Edward, Nov 3: "something more visual than just Done"): a tick and the accent edge.
    <li className={`rounded-[16px] border p-4 ${done ? "border-accent-text bg-soft" : "border-line bg-ground"}`} data-testid={`invite-${invite.slot}`}>
      <div className="flex items-center justify-between gap-3">
        <span className="min-w-0">
          <span className="block text-[12px] font-semibold uppercase tracking-[0.06em] text-muted">Link {invite.slot}</span>
          <span className="heading block text-[16px]">{forWhom ?? `Link ${invite.slot}`}</span>
        </span>
        {done ? (
          <span className="flex shrink-0 items-center gap-1.5 text-[14px] font-bold text-accent-text">
            <span aria-hidden="true" className="grid size-6 place-items-center rounded-full bg-accent-text text-panel">
              <svg viewBox="0 0 16 16" width={14} height={14} fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round">
                <path d="M3.5 8.5l3 3 6-7" />
              </svg>
            </span>
            {STATUS_LABEL.completed}
          </span>
        ) : (
          <span className="shrink-0 text-[13px] text-muted">{STATUS_LABEL[invite.status]}</span>
        )}
      </div>
      {done ? null : (
        <>
          <p className="mt-2 truncate text-[13px] text-muted">{invite.url}</p>
          <div className="mt-3 flex gap-2">
            {phone ? (
              // "sms:?&body=" opens Messages with the text filled in on both iOS and Android.
              <a href={`sms:?&body=${encodeURIComponent(message)}`} className={`${button} btn-primary`} onClick={() => onShared("text")}>
                Text
              </a>
            ) : (
              <a href={`mailto:?subject=${encodeURIComponent("Would you answer a few questions about me?")}&body=${encodeURIComponent(message)}`} className={`${button} btn-primary`} onClick={() => onShared("text")}>
                Email
              </a>
            )}
            {canShare ? (
              <button type="button" className={`${button} btn-secondary`} onClick={share}>
                Share
              </button>
            ) : null}
            <button type="button" className={`${button} btn-secondary`} onClick={copy}>
              {copied ? "Copied" : "Copy"}
            </button>
          </div>
          <span className="sr-only" role="status">
            {copied ? `${title} copied, with a message` : ""}
          </span>
        </>
      )}
    </li>
  );
}
